/**
 * Notification Statistics Component
 * 
 * Shows user engagement with notifications
 */

import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { TrendingUp, Bell, MousePointerClick, Calendar } from 'lucide-react';
import { getNotificationStats, getNotificationEffectivenessScore } from '../lib/notification-analytics';

export function NotificationStats() {
  const [stats, setStats] = useState<any>(null);
  const [score, setScore] = useState(0);

  useEffect(() => {
    setStats(getNotificationStats());
    setScore(getNotificationEffectivenessScore());
  }, []);

  if (!stats || stats.last30Days.delivered === 0) {
    return null;
  }

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="font-medium mb-1">Notification Insights</h3>
        <p className="text-sm text-neutral-600">Your engagement over the last 30 days</p>
      </div>

      {/* Effectiveness Score */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium">Effectiveness Score</span>
          </div>
          <span className="text-2xl font-light">{score}%</span>
        </div>
        <Progress value={score} className="h-2" />
        <p className="text-xs text-neutral-600">
          {score >= 80 && "Excellent! You're staying consistent with check-ins."}
          {score >= 60 && score < 80 && "Great engagement! Keep up the momentum."}
          {score >= 40 && score < 60 && "Good start! Try to check in more consistently."}
          {score < 40 && "Notifications can help. Try enabling daily reminders."}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5 text-neutral-500" />
            <p className="text-xs text-neutral-600">Sent</p>
          </div>
          <p className="text-xl font-light">{stats.last30Days.delivered}</p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <MousePointerClick className="w-3.5 h-3.5 text-neutral-500" />
            <p className="text-xs text-neutral-600">Clicked</p>
          </div>
          <p className="text-xl font-light">{stats.last30Days.clicked}</p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-neutral-500" />
            <p className="text-xs text-neutral-600">Rate</p>
          </div>
          <p className="text-xl font-light">{stats.last30Days.clickRate.toFixed(0)}%</p>
        </div>
      </div>

      {/* Last 7 Days */}
      <div className="pt-4 border-t border-neutral-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-neutral-600">
            <Calendar className="w-4 h-4" />
            <span>Last 7 Days</span>
          </div>
          <div className="text-right">
            <span className="font-medium">{stats.last7Days.delivered}</span>
            <span className="text-neutral-600"> sent, </span>
            <span className="font-medium">{stats.last7Days.clicked}</span>
            <span className="text-neutral-600"> clicked</span>
          </div>
        </div>
      </div>

      {/* Tips */}
      {stats.last30Days.clickRate < 50 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-900">
            💡 <strong>Tip:</strong> Notifications work best when you act on them quickly. 
            Try setting your reminder time closer to when you naturally reflect on your day.
          </p>
        </div>
      )}
    </Card>
  );
}
